import { useEffect, useRef, useCallback, useState } from "react";
import backend from "../apis/backend";
import {
  acquireDeliverySheetLock,
  releaseDeliverySheetLock,
  heartbeatDeliverySheetLock,
} from "../apis/backendRequests";

// El backend vence el bloqueo a los 10 minutos sin renovar
const HEARTBEAT_INTERVAL = 2 * 60 * 1000;

const getLockError = (err) => err?.response?.data?.errors?.[0];

// Al cerrar la pestaña axios no llega a enviar el release: fetch con keepalive sí
// (sendBeacon no sirve porque no manda X-Lock-Id)
const releaseOnPageHide = (sheetId) => {
  const lockId = sessionStorage.getItem("lockId");
  if (!lockId) return;

  const baseURL = (backend.defaults.baseURL || "").replace(/\/$/, "");
  fetch(`${baseURL}/deliverysheet/${sheetId}/lock/release`, {
    method: "POST",
    keepalive: true,
    headers: {
      Authorization: backend.defaults.headers.common["Authorization"],
      "X-Lock-Id": lockId,
    },
  }).catch(() => {});
};

/**
 * Hook reactivo para bloqueo de planillas de reparto (mismo funcionamiento que en Tejidos Portal).
 * Adquiere el lock cuando sheetId es truthy,
 * lo libera automáticamente cuando cambia o se desmonta.
 *
 * IMPORTANTE: llamar await releaseLock() de forma explícita
 * antes de cualquier acción que limpie el estado de la planilla
 * (limpiar, abrir otra, etc.).
 *
 * @param {number} sheetId - Planilla a bloquear (vacío = sin lock)
 * @param {Function} onLockLost - Callback cuando no se obtiene o se pierde el lock; recibe { errorCode, errorDescription }
 * @returns {{ releaseLock: Function, hasLock: boolean }}
 */
export default function useDocumentLock(sheetId, onLockLost) {
  const heartbeatRef = useRef(null);
  const isLockedRef = useRef(false);
  const onLockLostRef = useRef(onLockLost);
  const [hasLock, setHasLock] = useState(false);

  useEffect(() => {
    onLockLostRef.current = onLockLost;
  });

  const stopHeartbeat = useCallback(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  }, []);

  const clearLockState = useCallback(() => {
    isLockedRef.current = false;
    setHasLock(false);
    sessionStorage.removeItem("lockId");
    stopHeartbeat();
  }, [stopHeartbeat]);

  useEffect(() => {
    if (!sheetId) return;

    let cancelled = false;

    const acquire = async () => {
      try {
        const res = await acquireDeliverySheetLock(sheetId);
        if (cancelled) return;

        sessionStorage.setItem("lockId", res.data.lockId);
        isLockedRef.current = true;
        setHasLock(true);

        heartbeatRef.current = setInterval(async () => {
          try {
            await heartbeatDeliverySheetLock(sheetId);
          } catch (err) {
            if (!isLockedRef.current) return;
            clearLockState();
            onLockLostRef.current?.(getLockError(err));
          }
        }, HEARTBEAT_INTERVAL);
      } catch (err) {
        if (cancelled) return;
        clearLockState();
        onLockLostRef.current?.(getLockError(err));
      }
    };

    acquire();

    return () => {
      cancelled = true;

      if (isLockedRef.current) {
        const releasingLockId = sessionStorage.getItem("lockId");
        isLockedRef.current = false;
        setHasLock(false);
        stopHeartbeat();

        releaseDeliverySheetLock(sheetId)
          .catch(() => {})
          .finally(() => {
            if (sessionStorage.getItem("lockId") === releasingLockId) {
              sessionStorage.removeItem("lockId");
            }
          });
      }
    };
  }, [sheetId, stopHeartbeat, clearLockState]);

  useEffect(() => {
    const handleLockLost = (e) => {
      if (!isLockedRef.current) return;
      clearLockState();
      onLockLostRef.current?.(e.detail);
    };

    window.addEventListener("lock-lost", handleLockLost);
    return () => window.removeEventListener("lock-lost", handleLockLost);
  }, [clearLockState]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isLockedRef.current) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    const handlePageHide = () => {
      if (isLockedRef.current) releaseOnPageHide(sheetId);
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("pagehide", handlePageHide);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, [sheetId]);

  const releaseLock = useCallback(async () => {
    if (!isLockedRef.current) return;

    isLockedRef.current = false;
    setHasLock(false);
    stopHeartbeat();

    try {
      await releaseDeliverySheetLock(sheetId);
    } catch (error) {
      console.error("Release lock failed", error);
    } finally {
      sessionStorage.removeItem("lockId");
    }
  }, [sheetId, stopHeartbeat]);

  return { releaseLock, hasLock };
}
